import { useEffect, useId, useRef, useState } from 'react'
import Portal from '../../components/Basics/Portal'
import { buildNotificationDetail } from './buildNotificationDetail'
import {
  DetailOverlay,
  DetailPanel,
  DetailHeader,
  DetailTitleRow,
  DetailTitle,
  DetailClose,
  DetailMetaRow,
  DetailTypeBadge,
  DetailTime,
  DetailBody,
  DetailSection,
  DetailSectionTitle,
  DetailMessage,
  DetailKvList,
  DetailKvItem,
  DetailKvKey,
  DetailKvValue,
  DetailFooter,
  DetailCta,
} from './Notification.styles'

const CLOSE_MS = 280
const DRAWER_ID = 'notification-detail-panel'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const NotificationDetailDrawer = ({ notification, onClose, onAction }) => {
  const titleId = useId()
  const panelRef = useRef(null)
  const closeRef = useRef(null)
  const closeTimerRef = useRef(null)
  const settledRef = useRef(true)
  const skipRestoreRef = useRef(false)
  const pendingActionRef = useRef(null)
  const [displayedNotification, setDisplayedNotification] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const finishClose = () => {
    if (settledRef.current) return
    settledRef.current = true
    clearCloseTimer()
    setDisplayedNotification(null)
    setIsOpen(false)
    document.body.style.overflow = ''
    const action = pendingActionRef.current
    pendingActionRef.current = null
    onClose?.()
    if (action) {
      window.setTimeout(() => onAction?.(action), 50)
    }
  }

  const beginClose = () => {
    if (settledRef.current || !isOpen) return
    setIsOpen(false)

    if (prefersReducedMotion()) {
      finishClose()
      return
    }

    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(finishClose, CLOSE_MS)
  }

  useEffect(() => {
    if (notification) {
      settledRef.current = false
      skipRestoreRef.current = false
      pendingActionRef.current = null
      clearCloseTimer()
      setDisplayedNotification(notification)

      const frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!settledRef.current) setIsOpen(true)
        })
      })
      return () => window.cancelAnimationFrame(frame)
    }

    if (!settledRef.current) {
      setIsOpen(false)
      if (prefersReducedMotion()) {
        settledRef.current = true
        setDisplayedNotification(null)
        return undefined
      }
      clearCloseTimer()
      closeTimerRef.current = window.setTimeout(() => {
        settledRef.current = true
        setDisplayedNotification(null)
        closeTimerRef.current = null
      }, CLOSE_MS)
    }
    return undefined
  }, [notification])

  useEffect(() => {
    if (!displayedNotification || !isOpen) return undefined

    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => {
      closeRef.current?.focus()
    }, 40)

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        beginClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      if (
        !skipRestoreRef.current &&
        previouslyFocused instanceof HTMLElement
      ) {
        previouslyFocused.focus()
      }
    }
  }, [displayedNotification, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      clearCloseTimer()
    },
    []
  )

  if (!displayedNotification) return null

  const detail = buildNotificationDetail(displayedNotification)
  if (!detail) return null

  const showCta = Boolean(detail.cta)

  const handlePanelTransitionEnd = (event) => {
    if (event.target !== panelRef.current) return
    if (event.propertyName !== 'transform') return
    if (isOpen) return
    finishClose()
  }

  const handleCtaClick = () => {
    if (!detail.cta) return
    skipRestoreRef.current = true
    pendingActionRef.current = detail.cta
    beginClose()
  }

  return (
    <Portal>
      <DetailOverlay $open={isOpen} onClick={beginClose} role="presentation">
        <DetailPanel
          id={DRAWER_ID}
          ref={panelRef}
          $open={isOpen}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
          onTransitionEnd={handlePanelTransitionEnd}
        >
          <DetailHeader>
            <DetailTitleRow>
              <DetailTitle id={titleId}>{detail.title}</DetailTitle>
              <DetailClose ref={closeRef} type="button" onClick={beginClose}>
                Cerrar
              </DetailClose>
            </DetailTitleRow>
            <DetailMetaRow>
              <DetailTypeBadge>{detail.typeLabel}</DetailTypeBadge>
              {detail.createdAtLabel ? (
                <DetailTime>{detail.createdAtLabel}</DetailTime>
              ) : null}
            </DetailMetaRow>
          </DetailHeader>

          <DetailBody>
            {detail.message ? (
              <DetailSection>
                <DetailSectionTitle>Mensaje</DetailSectionTitle>
                <DetailMessage>{detail.message}</DetailMessage>
              </DetailSection>
            ) : null}

            {detail.facts.length > 0 ? (
              <DetailSection>
                <DetailSectionTitle>Detalle</DetailSectionTitle>
                <DetailKvList>
                  {detail.facts.map((fact) => (
                    <DetailKvItem key={fact.key}>
                      <DetailKvKey>{fact.label}</DetailKvKey>
                      <DetailKvValue>{fact.value}</DetailKvValue>
                    </DetailKvItem>
                  ))}
                </DetailKvList>
              </DetailSection>
            ) : null}
          </DetailBody>

          {showCta ? (
            <DetailFooter>
              <DetailCta type="button" onClick={handleCtaClick}>
                {detail.cta.label}
              </DetailCta>
            </DetailFooter>
          ) : null}
        </DetailPanel>
      </DetailOverlay>
    </Portal>
  )
}

export default NotificationDetailDrawer
export { DRAWER_ID }
